import { BaseFeature } from '../../core/base-feature.js';

/**
 * WindowRearrangeFeature - Frontend feature for remote window rearrangement
 *
 * Provides API for shuffling, cascading, drifting, and manipulating
 * individual windows on the desktop.
 */
export class WindowRearrangeFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);

        this.pendingRequests = new Map();
        this.state = {
            windows: [],
            activeDrifts: []
        };
    }

    static getMetadata() {
        return {
            id: 'window-rearrange',
            name: 'Window Rearrange',
            version: 'v1.0',
            capabilities: [
                'shuffle_all',
                'cascade_all',
                'move_window',
                'resize_window',
                'minimize_window',
                'maximize_window',
                'restore_window',
                'drift_start',
                'drift_stop',
                'drift_stop_all',
                'get_state'
            ],
            dependencies: []
        };
    }

    async initialize() {
        console.log('[WindowRearrange] Initialized');
    }

    handleMessage(message) {
        const { action, payload } = message;

        // Handle responses to pending requests
        if (action?.endsWith('_response')) {
            const reqId = payload?.originalMessageId;
            if (reqId && this.pendingRequests.has(reqId)) {
                this.pendingRequests.get(reqId)(payload);
                this.pendingRequests.delete(reqId);
            }
            return;
        }

        // Handle state updates broadcast from desktop
        if (action === 'state_update') {
            if (payload?.windows) this.state.windows = payload.windows;
            if (payload?.activeDrifts) this.state.activeDrifts = payload.activeDrifts;
            this.emit('state_update', this.state);
        }
    }

    /**
     * Send a request and wait for response
     */
    _sendRequest(action, payload = {}) {
        return new Promise((resolve) => {
            const msgId = this.sendMessage(action, payload);
            this.pendingRequests.set(msgId, resolve);
            setTimeout(() => {
                if (this.pendingRequests.has(msgId)) {
                    this.pendingRequests.delete(msgId);
                    resolve({ success: false, error: 'Timeout' });
                }
            }, 10000);
        });
    }

    // Public API
    shuffleAll() { return this._sendRequest('shuffle_all'); }
    cascadeAll() { return this._sendRequest('cascade_all'); }
    moveWindow(hwnd, x, y) { return this._sendRequest('move_window', { hwnd, x, y }); }
    resizeWindow(hwnd, w, h) { return this._sendRequest('resize_window', { hwnd, w, h }); }
    minimizeWindow(hwnd) { return this._sendRequest('minimize_window', { hwnd }); }
    maximizeWindow(hwnd) { return this._sendRequest('maximize_window', { hwnd }); }
    restoreWindow(hwnd) { return this._sendRequest('restore_window', { hwnd }); }
    startDrift(hwnd, dx, dy, intervalMs) {
        return this._sendRequest('drift_start', { hwnd, dx, dy, intervalMs });
    }
    stopDrift(hwnd) { return this._sendRequest('drift_stop', { hwnd }); }
    stopAllDrifts() { return this._sendRequest('drift_stop_all'); }
    getState() { return this._sendRequest('get_state'); }

    async cleanup() {
        this.pendingRequests.clear();
    }
}
